import React, { useMemo } from 'react';

import Link from '@dashboard/components/mui-atoms/Link';
import { useWorkflowEventsQuery } from '@dashboard/generated/wp/platform';
import { useWorkflowsListHref } from '@dashboard/hooks/route/hrefs';
import { OpenInNewOutlined, SyncOutlined } from '@mui/icons-material';
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';

import useCurrentWorkflowId from '../../useCurrentWorkflowId';

import TriggerHistoryResults from './TriggerHistoryResults';

type Props = {
  disabled: boolean;
};
export default function TriggerHistoryBox({ disabled }: Props) {
  const workflowId = useCurrentWorkflowId();
  const workflowsListHref = useWorkflowsListHref();
  const { loading, data, refetch } = useWorkflowEventsQuery({
    notifyOnNetworkStatusChange: true,
    variables: { filter: { workflowId } },
  });

  const workflowRuns = useMemo(() => {
    if (!data) {
      return [];
    }
    return data.WorkflowEvents.nodes.filter((n) => n.source === 'API');
  }, [data]);

  const onClickRefetch = async () => {
    await refetch();
  };

  if (disabled) {
    return null;
  }
  return (
    <Stack
      sx={{
        overflow: 'auto',
        height: '100%',
        flexShrink: 0,
        borderRight: '1px solid',
        borderColor: 'divider',
        width: 216,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        sx={{ py: 1.5, px: 2, borderBottom: '1px solid', borderColor: 'divider', flexGrow: 0 }}
      >
        <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1 }}>
          Trigger history
        </Typography>
        <Link
          href={workflowsListHref}
          color="text.secondary"
          target="_blank"
          underline="hover"
          sx={{ fontSize: 12, fontWeight: 'fontWeightMedium', lineHeight: 1 }}
        >
          <Tooltip title="View full activity log">
            <OpenInNewOutlined sx={{ fontSize: 15, color: 'text.secondary' }} />
          </Tooltip>
        </Link>
      </Stack>
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <TriggerHistoryResults workflowRuns={workflowRuns} loading={loading} />
      </Box>
      <Box sx={{ borderTop: '1px solid', borderColor: 'divider', flexGrow: 0 }}>
        <Tooltip title="Check for new triggers">
          <Button disabled={loading} onClick={onClickRefetch} size="small" fullWidth sx={{ py: 0.75 }}>
            <SyncOutlined fontSize="small" sx={{ color: 'text.secondary' }} />
          </Button>
        </Tooltip>
      </Box>
    </Stack>
  );
}
